import apiClient from './api';
import {
  MediaLibraryItem,
  MediaLibraryResponse,
  MediaLibraryFilters,
  StorageStats,
} from '@/types/media-library';

/**
 * Media Library Service
 *
 * API calls for centralized media library management.
 * Supports both post-attached media and standalone library assets.
 */

/**
 * Build query string from filters
 */
const buildQueryString = (filters: MediaLibraryFilters): string => {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.typeGroup) params.append('typeGroup', filters.typeGroup);
  if (filters.search) params.append('search', filters.search);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.sort) params.append('sort', filters.sort);

  return params.toString();
};

export const mediaLibraryService = {
  /**
   * List media with filters and pagination
   */
  list: async (
    filters: MediaLibraryFilters = {},
    page = 1,
    limit = 20
  ): Promise<MediaLibraryResponse> => {
    const queryString = buildQueryString(filters);
    const paginationParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const fullQueryString = [queryString, paginationParams.toString()]
      .filter(Boolean)
      .join('&');

    const response = await apiClient.get(
      `/api/media-library${fullQueryString ? `?${fullQueryString}` : ''}`
    );

    return {
      items: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Get single media item
   */
  getById: async (id: number): Promise<MediaLibraryItem> => {
    const response = await apiClient.get(`/api/media-library/${id}`);
    return response.data.data;
  },

  /**
   * Get storage statistics
   */
  getStats: async (): Promise<StorageStats> => {
    const response = await apiClient.get('/api/media-library/stats');
    return response.data.data;
  },

  /**
   * Promote a media upload to the library as a standalone asset.
   * The upload must be in READY status. Returns the created MediaLibraryItem.
   */
  promote: async (uploadId: string): Promise<MediaLibraryItem> => {
    const response = await apiClient.post('/api/media-library/promote', { uploadId });
    return response.data.data;
  },

  /**
   * Bulk delete media items by IDs.
   * Removes S3 files and detaches media from posts (posts are not deleted).
   * Returns a count of deleted and skipped (non-owned) items.
   * Note: axios requires { data: { ids } } to send a body with DELETE requests.
   */
  bulkDelete: async (ids: number[]): Promise<{ deleted: number; skipped: number }> => {
    const response = await apiClient.delete('/api/media-library/bulk', { data: { ids } });
    return response.data.data;
  },
};
